/**
 * This file is part of the Valkyrie Robotics public website.
 * © 2025 Valkyrie Robotics, LLC. All rights reserved.
 */

import { Helmet } from "react-helmet-async";

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  image?: string;
  type?: "website" | "article";
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  keywords?: string[];
  noindex?: boolean;
}

export function SEO({
  title,
  description,
  canonical,
  image = "/media/hero.jpg",
  type = "website",
  publishedTime,
  modifiedTime,
  author = "Valkyrie Robotics",
  keywords = [],
  noindex = false,
}: SEOProps) {
  const siteUrl = "https://www.valkyrie-robotics.com";
  const fullTitle =
    title === "Valkyrie Robotics" ? title : `${title} | Valkyrie Robotics`;
  const fullCanonical = canonical ? `${siteUrl}${canonical}` : siteUrl;
  const fullImage = image.startsWith("http") ? image : `${siteUrl}${image}`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="author" content={author} />
      {keywords.length > 0 && (
        <meta name="keywords" content={keywords.join(", ")} />
      )}
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Canonical URL */}
      <link rel="canonical" href={fullCanonical} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullCanonical} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:site_name" content="Valkyrie Robotics" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={fullCanonical} />
      <meta property="twitter:title" content={fullTitle} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={fullImage} />
      <meta property="twitter:creator" content="@valkyrierobotics" />

      {/* Article specific meta tags */}
      {type === "article" && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === "article" && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {type === "article" && (
        <meta property="article:author" content={author} />
      )}

      {/* Additional SEO meta tags */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="theme-color" content="#c4a23a" />
      <meta name="msapplication-TileColor" content="#c4a23a" />

      {/* Structured Data for Organization */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "EducationalOrganization",
          name: "Valkyrie Robotics",
          description:
            "Competitive VEX Robotics coaching for students in Forsyth County, GA. We offer VEX IQ and V5 programs to help students develop STEM skills through hands-on robotics.",
          url: siteUrl,
          logo: `${siteUrl}/media/valkyrie-logo.svg`,
          image: fullImage,
          address: {
            "@type": "PostalAddress",
            addressLocality: "Forsyth County",
            addressRegion: "GA",
            addressCountry: "US",
          },
          contactPoint: {
            "@type": "ContactPoint",
            telephone: "+1-470-239-0787",
            contactType: "customer service",
            email: "contact@valkyrie-robotics.com",
          },
          sameAs: [
            "https://www.instagram.com/valkyrierobotics",
            "https://www.facebook.com/valkyrierobotics",
          ],
          serviceArea: {
            "@type": "GeoCircle",
            geoMidpoint: {
              "@type": "GeoCoordinates",
              latitude: 34.2079,
              longitude: -84.1402,
            },
            geoRadius: "50000",
          },
        })}
      </script>
    </Helmet>
  );
}
